import { useEffect, useRef, useState } from "react";
import Sortable from "sortablejs";
import "./styles.css";
import { TasksListComponent } from "./TasksListComponent";
import { useDispatch, useSelector } from "react-redux";
import { projectTaskUpdationService } from "../../../services/projectDetails";
import { toast } from "react-toastify";
import GroupModal from "./GroupModal";
import AddTask from "./AddTask";
import MilestoneTooltipModal from "./MilestoneTooltipModal";
import { Tooltip as ReactTooltip } from "react-tooltip";
import MilestoneDetailsModal from "./MilestoneDetailsModal";
import TaskListOptions from "./TaskListOptions";
import Skeleton from "react-loading-skeleton";

const TasksLists = ({
  onRender,
  data,
  taskData,
  setTaskData,
  isAddTaskShown,
  setAddTaskIsShown,
  setCurrentMilestone,
  showMilestone,
  setShowMilestone,
  milestoneNames,
  setMilestoneNames,
  isButtonDisable,
}) => {
  const dispatch = useDispatch();
  const cachedStatusData = useSelector((state) => state.trashandStatus.status);

  const [mileStoneId, setMileStoneId] = useState("");
  const [mileStoneName, setMileStoneName] = useState("");
  const [milestoneContentVisible, setMilestoneContentVisible] = useState([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [renderer, setRenderer] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [correspondingMilestone, setCorrespondingMilestone] = useState({});
  const [nullTasks, setNullTasks] = useState([]);
  const [nullTaskPageCount, setNulltaskPageCount] = useState(9);
  const [tasksToShowByMilestone, setTasksToShowByMilestone] = useState(5);
  const [groupTasksToShow, setGroupTasksToShow] = useState({});
  const [milestoneDetails, setMilestoneDetails] = useState(false);
  const [milestoneId, setMilestoneId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [milestoneLoadingStates, setMilestoneLoadingStates] = useState({});
  const [fetchStatus, setFetchStatus] = useState([]);
  const [projectGroups, setProjectGroups] = useState([]);
  const [droppedId, setDroppedId] = useState("");
  const tasksSortableRef = useRef(null);
  const milestonesSortableRefs = useRef([]);
  const hasLoadedOnce = useRef(false);
  const loadedProjectId = useRef(null);

  useEffect(() => {
    const projectId = data?.project?.id;
    if (!projectId) return;
    if (loadedProjectId.current !== projectId) {
      hasLoadedOnce.current = false;
      loadedProjectId.current = projectId;
    }
    let cancelled = false;
    const fetchMileStone = async () => {
      // Only show the loading skeleton on the true first load for this
      // project - a background refresh (e.g. renderer toggling after a task
      // edit or a time log change) must not unmount the task list, or any
      // open TaskDetails panel nested inside it closes along with it.
      if (!hasLoadedOnce.current) {
        setLoading(true);
      }
      try {
        const response =
          await dispatch.newProjectDetails.getListTaskAndMilestones({
            project_id: projectId,
          });
        if (cancelled) return;
        setNullTasks(response?.data?.tasks);
        const milestoneResponse =
          await dispatch.newProjectDetails.getGetFirstMilestone({
            project_id: projectId,
          });
        if (cancelled) return;
        setShowMilestone([milestoneResponse.data]);
        if (cachedStatusData && cachedStatusData.length > 0) {
          setFetchStatus(cachedStatusData);
        } else {
          const statusResponse = await dispatch.trashandStatus.getStatusData();
          if (cancelled) return;
          setFetchStatus(statusResponse.data);
        }
        const index = response?.data?.milestones.findIndex(
          (milestone) => milestone._id === milestoneResponse.data.milestone._id
        );

        if (index !== -1 && index !== 0) {
          const firstMilestone = response?.data?.milestones[index];
          const updatedMilestones = [
            firstMilestone,
            ...response?.data?.milestones.filter((_, i) => i !== index),
          ];
          setMilestoneNames(updatedMilestones);
        } else {
          setMilestoneNames(response?.data?.milestones);
        }

        const milestonesLength = response?.data?.milestones?.length || 0;
        const milestoneContentVisibility = Array.from(
          { length: milestonesLength },
          (_, i) => !!milestoneContentVisible[i]
        );
        if (milestonesLength > 0) {
          milestoneContentVisibility[0] = true;
        }
        setMilestoneContentVisible(milestoneContentVisibility);
        if (milestoneId) {
          fetchMilestoneDetails(milestoneId);
        }
      } catch (error) {
        console.log(error);
      } finally {
        if (!cancelled) {
          setLoading(false);
          hasLoadedOnce.current = true;
        }
      }
    };
    fetchMileStone();
    return () => {
      cancelled = true;
    };
  }, [data?.project?.id, renderer, onRender]);

  useEffect(() => {
    const projectId = data?.project?.id;
    if (!projectId) return;
    let cancelled = false;
    const loadGroups = async () => {
      try {
        const groups = await dispatch.projectDetails.getGroupList({
          project_id: projectId,
        });
        if (!cancelled) setProjectGroups(groups || []);
      } catch (e) {
        // ignore
      }
    };
    loadGroups();
    return () => {
      cancelled = true;
    };
  }, [data?.project?.id, renderer]);

  useEffect(() => {
    const task = document.querySelector(".tasks");
    const milestone = document.querySelectorAll(".milestones");
    const projectId = data?.project?.id;

    // Cleanup existing instances before re-initializing
    if (tasksSortableRef.current) {
      tasksSortableRef.current.destroy();
      tasksSortableRef.current = null;
    }
    milestonesSortableRefs.current.forEach((inst) => inst && inst.destroy());
    milestonesSortableRefs.current = [];

    // Shared onEnd handler for every Sortable instance in this tree (the
    // unassigned-tasks pool, each milestone, and each group). Resolves the
    // drop target from evt.to and persists it, or reverts the drag if the
    // API call fails.
    const handleTaskDrop = async (evt) => {
      setDroppedId(evt.from.id);

      // Reordering within the same list doesn't change milestone/group
      // membership - nothing to persist. (This also protects against the
      // "fromTask" pool's own id being mistaken for a milestone id below.)
      if (evt.to === evt.from) return;

      const groupExistsCheck = (projectGroups || []).filter(
        (item) => item._id === evt.to.id
      );

      try {
        if (evt.to.id === "fromTask") {
          await projectTaskUpdationService({
            project_id: projectId,
            task_id: evt.item.id,
            milestone_id: null,
            group_id: null,
          });
        } else if (groupExistsCheck?.length > 0) {
          await projectTaskUpdationService({
            project_id: projectId,
            task_id: evt.item.id,
            milestone_id: groupExistsCheck[0].milestone_id,
            group_id: evt.to.id,
          });
          setDroppedId(evt.to.id);
        } else {
          await projectTaskUpdationService({
            project_id: projectId,
            task_id: evt.item.id,
            milestone_id: evt.to.id,
            group_id: null,
          });
          setDroppedId(evt.to.id);
        }
      } catch (err) {
        // Put the card back where it started so the board matches what's
        // actually saved.
        evt.from.insertBefore(evt.item, evt.from.children[evt.oldIndex] || null);
        toast.error(err?.message || "Couldn't move the task. Please try again.");
        return;
      }

      if (evt.to.classList.contains("milestones")) {
        const targetQuery = evt.to.querySelector(".target");
        if (targetQuery && !evt.item.contains(targetQuery)) {
          targetQuery.insertBefore(evt.item, targetQuery.firstChild);
        }
      }

      const element = document.getElementById(evt.item.id);
      if (element && element.classList.contains("hidden")) {
        element.classList.remove("hidden");
      }
    };

    if (task !== null) {
      const sortableTasks = new Sortable(task, {
        group: { name: "shared" },
        handle: ".handle",
        animation: 150,
        sort: true,
      });

      tasksSortableRef.current = sortableTasks;
      sortableTasks.options.onEnd = handleTaskDrop;

      for (let i = 0; i < milestone?.length; ++i) {
        const sortableNestedTasks = new Sortable(milestone[i], {
          group: "shared",
          filter: ".filter",
          animation: 150,
        });
        milestonesSortableRefs.current.push(sortableNestedTasks);
        sortableNestedTasks.options.onEnd = handleTaskDrop;
      }
    }

    return () => {
      if (tasksSortableRef.current) {
        tasksSortableRef.current.destroy();
        tasksSortableRef.current = null;
      }
      milestonesSortableRefs.current.forEach((inst) => inst && inst.destroy());
      milestonesSortableRefs.current = [];
    };
    // milestoneContentVisible is intentionally excluded: the .milestones drop
    // containers are always mounted (see comment above), only CSS-collapsed,
    // so toggling one milestone doesn't need to tear down and rebuild every
    // Sortable instance on the page. milestoneNames IS needed: the .milestones
    // divs themselves only exist once this list has loaded and rendered.
  }, [projectGroups, data?.project?.id, milestoneNames]);

  const toggleMilestoneHide = async (index, milestoneId) => {
    let element = document.getElementById(milestoneId);

    if (element && element.classList.contains("block")) {
      element.classList.remove("block");
    }
    if (element && element.classList.contains("hidden")) {
      element.classList.remove("hidden");
    }

    if (element) element.className += "hidden";
    const copyMilestoneContentVisible = [...milestoneContentVisible];
    copyMilestoneContentVisible[index] = !copyMilestoneContentVisible[index];
    setMilestoneContentVisible(copyMilestoneContentVisible);
  };

  const fetchMilestoneDetails = async (milestoneId, index) => {
    try {
      const response = await dispatch.newProjectDetails.getGetOneMilestone({
        milestone_id: milestoneId,
      });
      // The API can respond 200 with an error body ({ status, message }, no
      // `data`) - guard against that instead of pushing `undefined` into
      // showMilestone, which crashes every milestone's render afterwards.
      if (!response?.data?.milestone) {
        throw new Error(
          response?.message || "Failed to load milestone details."
        );
      }
      setShowMilestone((prevState) => {
        const existingMilestoneIndex = prevState.findIndex(
          (milestone) => milestone?.milestone?._id === milestoneId
        );

        if (existingMilestoneIndex !== -1) {
          const updatedMilestones = [...prevState];
          updatedMilestones[existingMilestoneIndex] = response.data;
          return updatedMilestones;
        } else {
          return [...prevState, response.data];
        }
      });
    } catch (error) {
      console.log(error);
      toast.error(
        error?.message || "Failed to load milestone details. Please try again."
      );
    }
  };

  const handleMilestoneDetails = async (milestone) => {
    try {
      const response = await dispatch.newProjectDetails.getGetOneMilestone({
        milestone_id: milestone?._id,
      });
      if (!response?.data?.milestone) {
        throw new Error(
          response?.message || "Failed to load milestone details."
        );
      }
      setCorrespondingMilestone(response.data);
      setMilestoneDetails(true);
    } catch (error) {
      console.log(error);
      toast.error(
        error?.message || "Failed to load milestone details. Please try again."
      );
    }
  };

  const toggleMilestoneView = async (index, milestoneId) => {
    setMilestoneLoadingStates((prevState) => ({
      ...prevState,
      [milestoneId]: true,
    }));
    try {
      setMilestoneId(milestoneId);
      let element = document.getElementById(milestoneId);
      if (element && element.classList.contains("hidden")) {
        element.classList.remove("hidden");
      } else {
        await fetchMilestoneDetails(milestoneId, index);
        if (element) {
          element.classList.add("block");
        }
      }

      const copyMilestoneContentVisible = [...milestoneContentVisible];
      copyMilestoneContentVisible[index] = !copyMilestoneContentVisible[index];
      setMilestoneContentVisible(copyMilestoneContentVisible);
    } catch (error) {
      console.log(error);
    } finally {
      setMilestoneLoadingStates((prevState) => ({
        ...prevState,
        [milestoneId]: false,
      }));
    }
  };

  const toggleGroupHide = (milestoneId, groupId) => {
    const key = `${milestoneId}_${groupId}`;
    let element = document.getElementById(groupId);
    if (element && element.classList.contains("hidden")) {
      element.classList.remove("hidden");
    }
    if (element) element.className += " block";

    setExpandedGroups((prevExpandedGroups) => {
      return {
        ...prevExpandedGroups,
        [key]: !prevExpandedGroups[key],
      };
    });
  };

  const toggleGroupView = (milestoneId, groupId) => {
    const key = `${milestoneId}_${groupId}`;
    let element = document.getElementById(groupId);

    if (element && element.classList.contains("block")) {
      element.classList.remove("block");
    }
    if (element) element.className += " hidden";

    setExpandedGroups((prevExpandedGroups) => {
      return {
        ...prevExpandedGroups,
        [key]: !prevExpandedGroups[key],
      };
    });
  };

  const handleGroupLoadMore = (groupId) => {
    setGroupTasksToShow((prevState) => ({
      ...prevState,
      [groupId]: (prevState[groupId] || 5) + 5,
    }));
  };

  return (
    <div className="w-auto">
      {loading ? (
        <div className="max-w-full grid grid-cols-1 gap-6 overflow-x-auto pt-1 pb-7 ">
          <Skeleton borderRadius={5} height={40} />{" "}
          <Skeleton borderRadius={5} height={40} />{" "}
          <Skeleton borderRadius={5} height={40} />{" "}
          <Skeleton borderRadius={5} height={40} />
        </div>
      ) : (
        <div className="w-auto all_tasks" id={data?.project?.id}>
          <>
            <div className="mb-10">
              <div id="fromTask" className="tasks mb-4 flex flex-col gap-3">
                {nullTasks?.slice(0, nullTaskPageCount).map((task, _index) => {
                  return (
                    <div id={task._id} key={task._id}>
                      {task.status_name !== "DELETE" && (
                        <TasksListComponent
                          renderer={renderer}
                          setRenderer={setRenderer}
                          data={data}
                          item={task}
                          taskData={taskData}
                          setTaskData={setTaskData}
                          handle="handle"
                          taskTime={task.timelog}
                          isButtonDisable={isButtonDisable}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              {nullTasks?.length > nullTaskPageCount && (
                <p
                  onClick={() => setNulltaskPageCount(nullTaskPageCount + 9)}
                  className="mt-4 font-bold text-xs pl-1 cursor-pointer flex items-center gap-1"
                >
                  <span className="text-theme-accent">Load more</span>
                  <span> {nullTasks.length - nullTaskPageCount} tasks</span>
                </p>
              )}
            </div>

            {/* tasks inside milestone */}
            <div className="flex flex-col gap-3">
              {milestoneNames.length !== 0 &&
                milestoneNames?.map((milestone, index) => {
                  const correspondingDetail = showMilestone?.find(
                    (detail) => detail?.milestone?._id === milestone._id
                  );
                  return (
                    <div
                      className="px-3 md:px-6 py-4 bg-theme-cream-2 rounded-theme-lg"
                      key={milestone?._id}
                    >
                      <div className="flex items-center justify-between filter check">
                        <div className="filter text-theme-fg-1 font-semibold text-xs sm:text-sm flex gap-1 sm:gap-2 cursor-pointer">
                          <span
                            className=" capitalize block max-w-[90px] sm:max-w-[280px] xl:max-w-[400px] 2xl:max-w-[500px] truncate"
                            onClick={() => {
                              handleMilestoneDetails(milestone);
                            }}
                          >
                            {milestone?.title}
                          </span>
                          <div className="flex items-center">
                            {milestoneContentVisible[index] && (
                              <svg
                                width="13"
                                height="13"
                                viewBox="0 0 13 13"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                data-tooltip-id={`milestone-tooltip-${milestone._id}`}
                              >
                                <g clipPath="url(#clip0_339_23956)">
                                  <path
                                    d="M6.5 12.1875C4.99158 12.1875 3.54494 11.5883 2.47833 10.5217C1.41172 9.45506 0.8125 8.00842 0.8125 6.5C0.8125 4.99158 1.41172 3.54494 2.47833 2.47833C3.54494 1.41172 4.99158 0.8125 6.5 0.8125C8.00842 0.8125 9.45506 1.41172 10.5217 2.47833C11.5883 3.54494 12.1875 4.99158 12.1875 6.5C12.1875 8.00842 11.5883 9.45506 10.5217 10.5217C9.45506 11.5883 8.00842 12.1875 6.5 12.1875ZM6.5 13C8.22391 13 9.87721 12.3152 11.0962 11.0962C12.3152 9.87721 13 8.22391 13 6.5C13 4.77609 12.3152 3.12279 11.0962 1.90381C9.87721 0.68482 8.22391 0 6.5 0C4.77609 0 3.12279 0.68482 1.90381 1.90381C0.68482 3.12279 0 4.77609 0 6.5C0 8.22391 0.68482 9.87721 1.90381 11.0962C3.12279 12.3152 4.77609 13 6.5 13Z"
                                    fill="#8A8A82"
                                  />
                                  <path
                                    d="M5.68915 8.93766C5.68915 8.83096 5.71016 8.7253 5.751 8.62673C5.79183 8.52815 5.85168 8.43858 5.92712 8.36313C6.00257 8.28768 6.09214 8.22784 6.19072 8.187C6.28929 8.14617 6.39495 8.12516 6.50165 8.12516C6.60835 8.12516 6.714 8.14617 6.81258 8.187C6.91116 8.22784 7.00072 8.28768 7.07617 8.36313C7.15162 8.43858 7.21147 8.52815 7.2523 8.62673C7.29313 8.7253 7.31415 8.83096 7.31415 8.93766C7.31415 9.15315 7.22855 9.35981 7.07617 9.51218C6.9238 9.66455 6.71714 9.75016 6.50165 9.75016C6.28616 9.75016 6.0795 9.66455 5.92712 9.51218C5.77475 9.35981 5.68915 9.15315 5.68915 8.93766ZM5.76877 4.05859C5.75795 3.95609 5.7688 3.85246 5.80062 3.75442C5.83244 3.65638 5.88452 3.56612 5.95347 3.48951C6.02242 3.41289 6.10671 3.35163 6.20087 3.3097C6.29503 3.26776 6.39695 3.24609 6.50002 3.24609C6.6031 3.24609 6.70502 3.26776 6.79918 3.3097C6.89333 3.35163 6.97762 3.41289 7.04658 3.48951C7.11553 3.56612 7.1676 3.65638 7.19942 3.75442C7.23124 3.85246 7.24209 3.95609 7.23127 4.05859L6.9469 6.90803C6.93734 7.01997 6.88612 7.12425 6.80337 7.20024C6.72062 7.27622 6.61237 7.31839 6.50002 7.31839C6.38768 7.31839 6.27942 7.27622 6.19667 7.20024C6.11392 7.12425 6.0627 7.01997 6.05315 6.90803L5.76877 4.05859Z"
                                    fill="#8A8A82"
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_339_23956">
                                    <rect width="13" height="13" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                            )}
                          </div>
                          {correspondingDetail?.milestone
                            ?.is_current_milestone && (
                            <button className="bg-theme-avail-fg h-fit px-2 rounded text-white text-[9px]">
                              Current
                            </button>
                          )}
                        </div>

                        <div className="flex justify-between items-center text-xs space-x-2  sm:space-x-5 filter text-theme-fg-2">
                          <div className="flex items-center gap-[2px] sm:gap-1">
                            <svg
                              className="w-3 h-3 sm:w-4 sm:h-4"
                              viewBox="0 0 17 17"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M9.2082 2.83301H7.79181V7.79134H2.83445V9.20801H7.79181V14.1663H9.2082V9.20801H14.1656V7.79134H9.2082V2.83301Z"
                                fill="#0C7A5A"
                              />
                            </svg>

                            <button
                              className="text-theme-avail-fg text-xs font-medium flex items-center gap-1"
                              onClick={() => {
                                setShowAddTask(true);
                                setMileStoneId(milestone?._id);
                                setMileStoneName(milestone?.title);
                              }}
                            >
                              <span className="hidden md:block">Add </span>Task
                            </button>
                          </div>
                          <div className="flex items-center gap-[2px] sm:gap-1">
                            <svg
                              viewBox="0 0 17 17"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-3 h-3 sm:w-4 sm:h-4"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M9.2082 2.83301H7.79181V7.79134H2.83445V9.20801H7.79181V14.1663H9.2082V9.20801H14.1656V7.79134H9.2082V2.83301Z"
                                fill="#0C7A5A"
                              />
                            </svg>
                            <button
                              className="text-theme-avail-fg font-medium text-xs flex items-center gap-1"
                              onClick={() => {
                                setShowGroupModal(true);
                                setMileStoneId(milestone?._id);
                              }}
                            >
                              <span className="hidden md:block">Add</span>Group
                            </button>
                          </div>
                          <div>
                            <TaskListOptions
                              milestone={milestone}
                              data={data}
                              setCurrentMilestone={setCurrentMilestone}
                              currentMilestone={
                                correspondingDetail?.milestone
                                  ?.is_current_milestone
                              }
                              setRenderer={setRenderer}
                              renderer={renderer}
                              correspondingDetail={correspondingDetail}
                              isButtonDisable={isButtonDisable}
                            />
                          </div>
                          {milestoneContentVisible[index] ? (
                            <svg
                              width="14"
                              height="15"
                              viewBox="0 0 14 15"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className={`cursor-pointer select-none`}
                              onClick={() => {
                                toggleMilestoneHide(index, milestone?._id);
                              }}
                            >
                              <path
                                d="M2.04136 9.83993C2.11949 9.91625 2.22438 9.95898 2.33361 9.95898C2.44284 9.95898 2.54773 9.91625 2.62586 9.83993L6.99999 5.57605L11.3732 9.83993C11.4514 9.91625 11.5563 9.95898 11.6655 9.95898C11.7747 9.95898 11.8796 9.91625 11.9577 9.83993C11.9958 9.80283 12.0261 9.75848 12.0468 9.70948C12.0675 9.66049 12.0781 9.60785 12.0781 9.55468C12.0781 9.5015 12.0675 9.44886 12.0468 9.39987C12.0261 9.35088 11.9958 9.30652 11.9577 9.26943L7.30536 4.73518C7.22366 4.65554 7.11408 4.61097 6.99999 4.61097C6.88589 4.61097 6.77631 4.65554 6.69461 4.73518L2.04224 9.26943C2.00414 9.30652 1.97385 9.35088 1.95318 9.39987C1.9325 9.44886 1.92184 9.5015 1.92184 9.55468C1.92184 9.60785 1.9325 9.66049 1.95318 9.70948C1.97385 9.75848 2.00414 9.80283 2.04224 9.83993H2.04136Z"
                                fill="#4C4C46"
                              />
                            </svg>
                          ) : (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className={`cursor-pointer select-none ${
                                milestoneLoadingStates[milestone._id]
                                  ? "animate-spin -scale-x-100 curs"
                                  : ""
                              }`}
                              onClick={() => {
                                toggleMilestoneView(index, milestone._id);
                              }}
                            >
                              <path
                                d="M4.66026 2.04139C4.58393 2.11952 4.5412 2.22442 4.5412 2.33364C4.5412 2.44287 4.58393 2.54776 4.66026 2.62589L8.92413 7.00002L4.66026 11.3733C4.58393 11.4514 4.5412 11.5563 4.5412 11.6655C4.5412 11.7747 4.58393 11.8796 4.66026 11.9578C4.69735 11.9959 4.74171 12.0261 4.7907 12.0468C4.83969 12.0675 4.89233 12.0782 4.94551 12.0782C4.99868 12.0782 5.05132 12.0675 5.10031 12.0468C5.14931 12.0261 5.19366 11.9959 5.23076 11.9578L9.76501 7.30539C9.84464 7.22369 9.88921 7.11411 9.88921 7.00002C9.88921 6.88592 9.84464 6.77634 9.76501 6.69464L5.23076 2.04227C5.19366 2.00417 5.14931 1.97388 5.10031 1.95321C5.05132 1.93253 4.99868 1.92188 4.94551 1.92188C4.89233 1.92188 4.83969 1.93253 4.7907 1.95321C4.74171 1.97388 4.69735 2.00417 4.66026 2.04227V2.04139Z"
                                fill="#4C4C46"
                              />
                            </svg>
                          )}
                        </div>
                      </div>

                      <div
                        id={milestone?._id}
                        className={`transition-all duration-500 ease-in-out overflow-visible ${
                          milestoneContentVisible[index]
                            ? "max-h-[4000px] opacity-100 pt-3"
                            : "max-h-0 opacity-0 py-0"
                        }`}
                      >
                        <ReactTooltip
                          id={`milestone-tooltip-${milestone._id}`}
                          variant="info"
                          style={{
                            backgroundColor: "white",
                            color: "black",
                          }}
                          className="border"
                          content={
                            <MilestoneTooltipModal
                              milestone={correspondingDetail}
                              fetchStatus={fetchStatus}
                            />
                          }
                        />
                        <div
                          className="milestones flex flex-col gap-2"
                          id={milestone._id}
                        >
                          {/* Always render the drop container itself (even
                              when empty) so SortableJS can find it as a
                              valid drop target for cross-milestone drags. */}
                          {correspondingDetail?.groups?.length === 0 &&
                            correspondingDetail?.tasks?.length === 0 && (
                              <p
                                className={`${
                                  droppedId ===
                                  correspondingDetail?.milestone?._id
                                    ? "hidden"
                                    : " text-theme-fg-3 text-xs sm:text-sm pt-2"
                                }`}
                              >
                                No tasks found
                              </p>
                            )}
                            {/* tasks inside groups */}
                            {correspondingDetail?.groups?.map((group) => {
                              if (group.delete_status === "1") {
                                return (
                                  <div className="group_view" key={group._id}>
                                    <div className="border border-theme-cream-3 rounded-theme-lg py-3 px-2 sm:px-4 bg-theme-cream overflow-hidden">
                                      <div className="flex justify-between">
                                        <div className="filter flex items-center">
                                          {!expandedGroups[
                                            `${group.milestone_id}_${group._id}`
                                          ] ? (
                                            <svg
                                              width="14"
                                              height="14"
                                              viewBox="0 0 14 14"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                              onClick={() =>
                                                toggleGroupHide(
                                                  group.milestone_id,
                                                  group._id
                                                )
                                              }
                                              className="mr-2 cursor-pointer"
                                            >
                                              <path
                                                d="M4.66026 2.04139C4.58393 2.11952 4.5412 2.22442 4.5412 2.33364C4.5412 2.44287 4.58393 2.54776 4.66026 2.62589L8.92413 7.00002L4.66026 11.3733C4.58393 11.4514 4.5412 11.5563 4.5412 11.6655C4.5412 11.7747 4.58393 11.8796 4.66026 11.9578C4.69735 11.9959 4.74171 12.0261 4.7907 12.0468C4.83969 12.0675 4.89233 12.0782 4.94551 12.0782C4.99868 12.0782 5.05132 12.0675 5.10031 12.0468C5.14931 12.0261 5.19366 11.9959 5.23076 11.9578L9.76501 7.30539C9.84464 7.22369 9.88921 7.11411 9.88921 7.00002C9.88921 6.88592 9.84464 6.77634 9.76501 6.69464L5.23076 2.04227C5.19366 2.00417 5.14931 1.97388 5.10031 1.95321C5.05132 1.93253 4.99868 1.92188 4.94551 1.92188C4.89233 1.92188 4.83969 1.93253 4.7907 1.95321C4.74171 1.97388 4.69735 2.00417 4.66026 2.04227V2.04139Z"
                                                fill="#4C4C46"
                                              />
                                            </svg>
                                          ) : (
                                            <svg
                                              width="14"
                                              height="14"
                                              viewBox="0 0 14 14"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                              onClick={() =>
                                                toggleGroupView(
                                                  group.milestone_id,
                                                  group._id
                                                )
                                              }
                                              className="mr-2 cursor-pointer"
                                            >
                                              <path
                                                d="M11.9586 4.66007C11.8805 4.58375 11.7756 4.54102 11.6664 4.54102C11.5571 4.54102 11.4522 4.58375 11.3741 4.66007L6.99998 8.92395L2.62673 4.66007C2.5486 4.58375 2.44371 4.54102 2.33448 4.54102C2.22526 4.54102 2.12037 4.58375 2.04223 4.66007C2.00413 4.69717 1.97385 4.74152 1.95317 4.79052C1.9325 4.83951 1.92184 4.89215 1.92184 4.94532C1.92184 4.9985 1.9325 5.05114 1.95317 5.10013C1.97385 5.14912 2.00413 5.19348 2.04223 5.23057L6.69461 9.76482C6.77631 9.84446 6.88589 9.88903 6.99998 9.88903C7.11408 9.88903 7.22366 9.84446 7.30536 9.76482L11.9577 5.23057C11.9958 5.19348 12.0261 5.14912 12.0468 5.10013C12.0675 5.05114 12.0781 4.9985 12.0781 4.94532C12.0781 4.89215 12.0675 4.83951 12.0468 4.79052C12.0261 4.74152 11.9958 4.69717 11.9577 4.66007H11.9586Z"
                                                fill="#4C4C46"
                                              />
                                            </svg>
                                          )}

                                          <svg
                                            width="15"
                                            height="16"
                                            viewBox="0 0 15 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="mr-2.5"
                                          >
                                            <path
                                              d="M1.40627 5.78202L7.34377 9.20702C7.39127 9.23445 7.44516 9.24889 7.50002 9.24889C7.55487 9.24889 7.60876 9.23445 7.65627 9.20702L13.5938 5.78202C13.6413 5.75459 13.6807 5.71514 13.7081 5.66764C13.7356 5.62013 13.75 5.56625 13.75 5.51139C13.75 5.45654 13.7356 5.40265 13.7081 5.35515C13.6807 5.30765 13.6413 5.2682 13.5938 5.24077L7.65627 1.81827C7.60856 1.79144 7.55475 1.77734 7.50002 1.77734C7.44529 1.77734 7.39147 1.79144 7.34377 1.81827L1.40627 5.24077C1.35877 5.2682 1.31932 5.30765 1.2919 5.35515C1.26447 5.40265 1.25003 5.45654 1.25003 5.51139C1.25003 5.56625 1.26447 5.62013 1.2919 5.66764C1.31932 5.71514 1.35877 5.75459 1.40627 5.78202ZM7.50002 2.45077L12.8119 5.51202L7.50002 8.57639L2.18814 5.51202L7.50002 2.45077ZM13.28 10.2033L7.50002 13.5751L1.72002 10.2033C1.68456 10.1826 1.64538 10.1691 1.60471 10.1635C1.56403 10.158 1.52267 10.1605 1.48297 10.171C1.44327 10.1814 1.40602 10.1996 1.37334 10.2244C1.34067 10.2493 1.3132 10.2803 1.29252 10.3158C1.27184 10.3512 1.25834 10.3904 1.2528 10.4311C1.24726 10.4718 1.24978 10.5131 1.26023 10.5528C1.27068 10.5925 1.28884 10.6298 1.31368 10.6624C1.33853 10.6951 1.36956 10.7226 1.40502 10.7433L7.34252 14.207C7.39032 14.2349 7.44467 14.2496 7.50002 14.2496C7.55536 14.2496 7.60972 14.2349 7.65752 14.207L13.595 10.7433C13.6305 10.7226 13.6615 10.6951 13.6864 10.6624C13.7112 10.6298 13.7294 10.5925 13.7398 10.5528C13.7503 10.5131 13.7528 10.4718 13.7472 10.4311C13.7417 10.3904 13.7282 10.3512 13.7075 10.3158C13.6868 10.2803 13.6594 10.2493 13.6267 10.2244C13.594 10.1996 13.5568 10.1814 13.5171 10.171C13.4774 10.1605 13.436 10.158 13.3953 10.1635C13.3547 10.1691 13.3155 10.1826 13.28 10.2033ZM13.28 7.70327L7.50002 11.0751L1.72002 7.70327C1.68456 7.68258 1.64538 7.66909 1.60471 7.66355C1.56403 7.65801 1.52267 7.66053 1.48297 7.67098C1.44327 7.68143 1.40602 7.69959 1.37334 7.72443C1.34067 7.74928 1.3132 7.78031 1.29252 7.81577C1.27184 7.85122 1.25834 7.89041 1.2528 7.93108C1.24726 7.97175 1.24978 8.01312 1.26023 8.05282C1.27068 8.09251 1.28884 8.12977 1.31368 8.16244C1.33853 8.19512 1.36956 8.22258 1.40502 8.24327L7.34252 11.707C7.39032 11.7349 7.44467 11.7496 7.50002 11.7496C7.55536 11.7496 7.60972 11.7349 7.65752 11.707L13.595 8.24327C13.6305 8.22258 13.6615 8.19512 13.6864 8.16244C13.7112 8.12977 13.7294 8.09251 13.7398 8.05282C13.7503 8.01312 13.7528 7.97175 13.7472 7.93108C13.7417 7.89041 13.7282 7.85122 13.7075 7.81577C13.6868 7.78031 13.6594 7.74928 13.6267 7.72443C13.594 7.69959 13.5568 7.68143 13.5171 7.67098C13.4774 7.66053 13.436 7.65801 13.3953 7.66355C13.3547 7.66909 13.3155 7.68258 13.28 7.70327Z"
                                              fill="#4C4C46"
                                            />
                                          </svg>

                                          <p className="filter groups text-tiny text-theme-fg-1 font-semibold capitalize">
                                            {group.group_name}
                                          </p>
                                        </div>
                                        <div className="flex mt-2">
                                          <div>
                                            <TaskListOptions
                                              groupTasks={group}
                                              setRenderer={setRenderer}
                                              renderer={renderer}
                                              isButtonDisable={isButtonDisable}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                      <div
                                        className={`transition-all milestones duration-500 ease-in-out overflow-visible flex flex-col gap-2
    ${
      expandedGroups[`${group.milestone_id}_${group._id}`]
        ? "max-h-[5000px] opacity-100 visible mt-3 "
        : "max-h-0 opacity-0 invisible"
    }`}
                                        id={group?._id}
                                      >
                                        {group?.tasks?.length > 0 ? (
                                          group?.tasks.map((tasks) => {
                                            return (
                                              <div
                                                className="check"
                                                id={tasks._id}
                                                key={tasks._id}
                                              >
                                                <TasksListComponent
                                                  renderer={renderer}
                                                  setRenderer={setRenderer}
                                                  data={data}
                                                  item={tasks}
                                                  taskData={taskData}
                                                  setTaskData={setTaskData}
                                                  handle="handle"
                                                  isGroup={true}
                                                  taskTime={tasks.timelog}
                                                  isButtonDisable={
                                                    isButtonDisable
                                                  }
                                                />
                                              </div>
                                            );
                                          })
                                        ) : (
                                          <p
                                            className={`${
                                              droppedId === group?._id
                                                ? "hidden"
                                                : " text-theme-fg-3 text-sm pt-2"
                                            }`}
                                          >
                                            No tasks found
                                          </p>
                                        )}
                                        <div>
                                          {groupTasksToShow[group._id] <
                                            (group?.tasks?.length || 0) &&
                                            (group?.tasks?.length || 0) >=
                                              5 && (
                                              <p
                                                onClick={() =>
                                                  handleGroupLoadMore(group._id)
                                                }
                                                className="mt-1 pl-1 font-bold text-xs cursor-pointer flex items-center gap-1 py-1"
                                              >
                                                <span className="text-theme-accent">
                                                  Load more
                                                </span>
                                                <span>
                                                  {(group?.tasks?.length || 0) -
                                                    (groupTasksToShow[
                                                      group._id
                                                    ] || 5)}{" "}
                                                  tasks
                                                </span>
                                              </p>
                                            )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              } else {
                                return null;
                              }
                            })}

                            <div className="target"></div>

                            {correspondingDetail?.tasks
                              ?.slice(0, tasksToShowByMilestone)
                              .map((tasks) => {
                                return (
                                  <div
                                    className="check"
                                    id={tasks._id}
                                    key={tasks._id}
                                  >
                                    {tasks.status_name !== "DELETE" && (
                                      <TasksListComponent
                                        renderer={renderer}
                                        setRenderer={setRenderer}
                                        data={data}
                                        item={tasks}
                                        taskData={tasks}
                                        setTaskData={setTaskData}
                                        handle="handle"
                                        isAddTaskShown={isAddTaskShown}
                                        setAddTaskIsShown={setAddTaskIsShown}
                                        isButtonDisable={isButtonDisable}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                        </div>

                        {correspondingDetail?.tasks?.length >
                          tasksToShowByMilestone && (
                          <p
                            onClick={() =>
                              setTasksToShowByMilestone(
                                tasksToShowByMilestone + 5
                              )
                            }
                            className="mt-5 font-bold text-xs pl-1 cursor-pointer flex items-center gap-1"
                          >
                            <span className="text-theme-accent">Load more</span>
                            <span>
                              {" "}
                              {correspondingDetail.tasks.length -
                                tasksToShowByMilestone}{" "}
                              tasks
                            </span>
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>

            {milestoneDetails && (
              <MilestoneDetailsModal
                setMilestoneDetails={setMilestoneDetails}
                correspondingMilestone={correspondingMilestone}
                milestoneDetails={milestoneDetails}
              />
            )}

            {showAddTask && (
              <AddTask
                data={data}
                setTaskData={setTaskData}
                isAddTaskShown={showAddTask}
                setAddTaskIsShown={setShowAddTask}
                milestoneId={mileStoneId}
                mileStoneName={mileStoneName}
                setRenderer={setRenderer}
                renderer={renderer}
              />
            )}
            {showGroupModal && (
              <GroupModal
                data={data}
                modal={showGroupModal}
                setModal={setShowGroupModal}
                mileStoneId={mileStoneId}
                renderer={renderer}
                setRenderer={setRenderer}
              />
            )}
          </>
        </div>
      )}
    </div>
  );
};

export default TasksLists;
